import type { ApiCaseCatStacks } from '@/api/catStacks';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import type { ApiStudyLandmarks } from '@/api/study/studyLandmarks';
import type { SideId } from '@/domain/anatomy/side';
import { findLandmark } from '@/domain/anatomy/landmarks';
import { vector3 } from '@/domain/geometry/vector3';
import { catstacksData } from '@/api/catstacksData';
import { taggedLogger } from '@/util';

const log = taggedLogger('overlay-view');

/**
 * The view-state of a particular **planner** catstack
 */
export type OverlayCatstackState = {
    visible: boolean;

    /** The current slice-index */
    sliceIndex: number;

    /** The maximum possible slice-index */
    max: number;

    /** The minimum possible slice-index */
    min: number;
};

/**
 * The view-state of the catstacks
 */
export type OverlayCatstacksState = {

    /** View-state of the axial catstack */
    axial: OverlayCatstackState;

    /** View-state of the coronal catstack */
    coronal: OverlayCatstackState;

    sagittal: OverlayCatstackState;
};

/**
 * Create the view-state for the overlay catstacks
 */
export function overlayCatstacksState(
    data: ApiCaseCatStacks,
    landmarks: ApiStudyLandmarks,
    side: SideId,
    catstacks: ApiCaseCatStacks,
): OverlayCatstacksState {
    return {
        axial: {
            visible: true,
            min: 0,
            max: data.axial.count - 1,
            sliceIndex: calculateInitialSlice('axial', landmarks, side, catstacks),
        },
        coronal: {
            visible: true,
            min: 0,
            max: data.coronal.count - 1,
            sliceIndex: calculateInitialSlice('coronal', landmarks, side, catstacks),
        },
        sagittal: {
            visible: true,
            min: 0,
            max: data.sagittal.count - 1,
            sliceIndex: calculateInitialSlice('sagittal', landmarks, side, catstacks),
        },
    };
}

function calculateInitialSlice(
    planeId: CatstackPlaneId,
    { groups }: ApiStudyLandmarks,
    operative_side: SideId,
    catstacks: ApiCaseCatStacks,
): number {
    /**
     The initial slice index is the slice of the image that is closest to the head centre landmark
     */
    try {
        // Get the femur head centre landmark
        const headCentre = operative_side === 'left' ?
            findLandmark(groups, 'left-femur', 'hc') :
            findLandmark(groups, 'right-femur', 'hc');

        const headCentrePos = vector3(headCentre.coordinates);

        // Use the same calculation logic as the cross-section code
        const viewData = catstacksData(catstacks)[planeId];
        const { origin, direction, length } = viewData.axis;
        const maxSliceIndex = catstacks[planeId].count - 1;

        // Project the head centre position onto the view axis
        const headCenterVector = headCentrePos.clone().sub(origin);
        const projectionLength = headCenterVector.dot(direction);

        // Convert the projection to a normalised position (0 to 1)
        const normalisedPosition = Math.max(0, Math.min(1, projectionLength / length));

        // Convert normalised position to slice index
        const initialSliceIndex = Math.round(normalisedPosition * maxSliceIndex);

        log.info(`Setting optimal ${planeId} slice to ${initialSliceIndex} based on femur HC landmark projection`);

        return initialSliceIndex;
    } catch (error) {
        log.warn(`Could not calculate optimal ${planeId} slice, using default:`, error);
        return 0;
    }
}
